import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Button } from '@/components/primitives/button';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/** 頁面級錯誤邊界：錯誤文字貼近失敗內容，並提供重試（spec 24）。 */
export class PageErrorBoundary extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('頁面發生錯誤', error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <div
        role="alert"
        className="space-y-4 rounded-[var(--radius-surface)] border border-danger/40 bg-danger/5 p-6"
      >
        <div className="space-y-1.5">
          <p className="font-medium text-danger">這個頁面沒有順利載入</p>
          <p className="text-[0.875rem] leading-relaxed text-text-muted">
            你的資料沒有受影響，重新載入這個頁面就好。若持續發生，可以先到備份頁匯出一份資料。
          </p>
        </div>
        <p className="rounded-[var(--radius-control)] bg-surface-sunken px-3 py-2 font-mono text-[0.75rem] text-text-muted">
          {error.message}
        </p>
        <Button variant="secondary" size="sm" onClick={() => this.setState({ error: null })}>
          再試一次
        </Button>
      </div>
    );
  }
}
